import React, { useMemo } from 'react'
import { withStyles } from '@mui/styles'
import { Tooltip, Icon, Typography, Box } from '@mui/material'

import { Rating } from '@mui/material'

import { ratingInputStyles } from './rating-input.styles'

/**
 * @typedef {object} RatingInputChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop name.
 * @property {number | null} target.value - El valor de calificación seleccionado (1-5 estrellas, o null si se deselecciona).
 */

/**
 * @typedef {object} RatingInputProps
 * @property {object} classes - Objeto con los nombres de las clases CSS inyectado por withStyles.
 * @property {boolean} [readOnly=false] - Si es true, el rating se muestra de solo lectura sin permitir cambios.
 * @property {boolean} [required=false] - Indica si el campo es obligatorio.
 * @property {boolean} [explicit=false] - Si es true en modo readOnly, muestra el valor numérico junto a las estrellas.
 * @property {'small' | 'medium' | 'large'} [size='medium'] - Controla el tamaño de las estrellas en modo readOnly.
 * @property {number | null} [value] - El valor actual del rating (1-5).
 * @property {string} [title] - El título o etiqueta que se muestra sobre el rating.
 * @property {string} [name] - El nombre del campo, que se incluirá en el objeto de evento del onChange.
 * @property {string} [tooltip] - Texto informativo que se muestra en un icono de ayuda junto al título.
 * @property {'small' | 'normal'} [fontSize] - Controla el tamaño de la fuente del título.
 * @property {function(RatingInputChangeEvent): void} onChange - La función callback que se ejecuta cuando cambia el rating.
 */

/**
 * Un componente de calificación con estrellas que permite a los usuarios valorar de 1 a 5.
 *
 * Proporciona una interfaz visual con estrellas para seleccionar una calificación,
 * con soporte para modo de solo lectura, visualización explícita del valor numérico,
 * y tooltips informativos. Las estrellas son más grandes en modo editable para facilitar
 * la interacción del usuario.
 *
 * @component
 * @example
 * const MyReviewForm = () => {
 *   const [calificacion, setCalificacion] = useState(0);
 *
 *   const handleChange = (e) => {
 *     console.log(`El campo '${e.target.name}' ahora es: ${e.target.value} estrellas`);
 *     setCalificacion(e.target.value);
 *   };
 *
 *   return (
 *     <RatingInput
 *       title="Calificación del Servicio"
 *       name="calificacion"
 *       value={calificacion}
 *       onChange={handleChange}
 *       required
 *       tooltip="Califique el servicio de 1 a 5 estrellas"
 *     />
 *   );
 * };
 *
 * @param {RatingInputProps} props - Las props del componente RatingInput.
 */
export const RatingInput = withStyles(ratingInputStyles)(({
  classes,
  readOnly = false,
  explicit = false,
  size = 'medium',
  ...props
}) => {
  const tooltip = useMemo(() => {
    return props.tooltip ? (
      <span style={{ fontSize: 16, marginLeft: 4 }}>
        <Tooltip
          placement="top"
          title={
            <Typography variant="body2" style={{ whiteSpace: 'pre-line' }}>
              {props.tooltip}
            </Typography>
          }
        >
          <Icon fontSize="inherit">help_outline</Icon>
        </Tooltip>
      </span>
    ) : null
  }, [props.tooltip])

  const typographyVariant = props.fontSize === 'small' ? '2' : '1'

  const handleChange = (_, value) => {
    props.onChange({ target: { name: props.name, value } })
  }

  return (
    <>
      {props.title ? (
        <Typography variant={`subtitle${typographyVariant}`} className={classes.title}>
          {props.title + (tooltip ? tooltip : '')}
          {props.required ? <span style={{ color: '#B3272C' }}> *</span> : null}
        </Typography>
      ) : null}
      <Box display="flex" alignItems="center">
        <Rating
          {...{
            name: props.name,
            value: props.value,
            readOnly,
            size: readOnly ? size : 'large',
            onChange: handleChange,
          }}
        />
        {readOnly && explicit ? (
          <Box ml={1}>
            <Typography variant={`body${typographyVariant}`}>{props.value}</Typography>
          </Box>
        ) : null}
      </Box>
    </>
  )
})
